import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';

const RecruitmentAnalytics = () => {
  const unlockData = [
    { month: 'Jan', unlocks: 12, responses: 8 },
    { month: 'Feb', unlocks: 19, responses: 14 },
    { month: 'Mar', unlocks: 15, responses: 11 },
    { month: 'Apr', unlocks: 22, responses: 18 },
    { month: 'May', unlocks: 28, responses: 21 },
    { month: 'Jun', unlocks: 24, responses: 19 }
  ];

  const responseRateData = [
    { week: 'Week 1', rate: 65 },
    { week: 'Week 2', rate: 72 },
    { week: 'Week 3', rate: 68 },
    { week: 'Week 4', rate: 78 }
  ];

  const skillsData = [
    { name: 'React', value: 35, color: '#3B82F6' },
    { name: 'Node.js', value: 28, color: '#10B981' },
    { name: 'Python', value: 22, color: '#F59E0B' },
    { name: 'DevOps', value: 15, color: '#EF4444' }
  ];

  const stats = [
    {
      label: "Unlock Success Rate",
      value: "74%",
      change: "+5%",
      trend: "positive",
      icon: "TrendingUp"
    },
    {
      label: "Avg. Response Time",
      value: "2.3 days",
      change: "-0.5 days",
      trend: "positive",
      icon: "Clock"
    },
    {
      label: "Interview Conversion",
      value: "42%",
      change: "+8%",
      trend: "positive",
      icon: "Users"
    },
    {
      label: "Cost per Hire",
      value: "1,890 NOK",
      change: "-240 NOK",
      trend: "positive",
      icon: "DollarSign"
    }
  ];

  return (
    <div className="bg-card rounded-xl p-6 shadow-elevation border border-border">
      <h3 className="text-lg font-semibold text-foreground mb-6">Recruitment Analytics</h3>
      {/* Key Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats?.map((stat, index) => (
          <div key={index} className="p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name={stat?.icon} size={20} className="text-primary" />
              <span className={`text-sm font-medium ${
                stat?.trend === 'positive' ? 'text-success' : 'text-destructive'
              }`}>
                {stat?.change}
              </span>
            </div>
            <div className="text-2xl font-semibold text-foreground mb-1">{stat?.value}</div>
            <div className="text-sm text-muted-foreground">{stat?.label}</div>
          </div>
        ))}
      </div>
      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Unlocks vs Responses */}
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-4">Unlocks vs Responses</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={unlockData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="unlocks" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="responses" fill="var(--color-success)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Response Rate Trend */}
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-4">Response Rate Trend</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={responseRateData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="week" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  domain={[0, 100]}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`${value}%`, 'Response Rate']}
                />
                <Line 
                  type="monotone" 
                  dataKey="rate" 
                  stroke="var(--color-accent)" 
                  strokeWidth={3}
                  dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Skills Distribution */}
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-4">Top Skills Unlocked</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={skillsData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {skillsData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`${value}%`, 'Percentage']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            {skillsData?.map((skill, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: skill?.color }}
                />
                <span className="text-sm text-muted-foreground">
                  {skill?.name} ({skill?.value}%)
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Performance */}
        <div className="p-4 rounded-lg bg-muted/30 border border-border">
          <h4 className="font-medium text-foreground mb-4">This Month's Performance</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Profile Views</span>
              <div className="flex items-center space-x-2">
                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: '78%' }} />
                </div>
                <span className="text-sm font-medium text-foreground">156</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Unlocks</span>
              <div className="flex items-center space-x-2">
                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-success rounded-full" style={{ width: '65%' }} />
                </div>
                <span className="text-sm font-medium text-foreground">24</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Responses</span>
              <div className="flex items-center space-x-2">
                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-accent rounded-full" style={{ width: '72%' }} />
                </div>
                <span className="text-sm font-medium text-foreground">19</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Interviews</span>
              <div className="flex items-center space-x-2">
                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-warning rounded-full" style={{ width: '42%' }} />
                </div>
                <span className="text-sm font-medium text-foreground">8</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentAnalytics;